import { Environment, LeagueConfig } from '@leagueplatform/core';
import {
  AEROPLAN_ICON,
  ALMOST_THERE_ILLUSTRATION,
  BackButton,
  EN_STRING_OVERRIDES,
  ERROR_STATE_IMAGE,
  FR_STRING_OVERRIDES,
  manulifeTheme,
  SIGNUP_SUCCESS_ILLUSTRATION,
  TOOLTIP_ICON,
  TRANSPARENT_IMAGE,
} from '@leagueplatform/manulife-web-extensions';
import ML_CHEVRON_BACK from './assets/ml_chevron_back.svg';
import ML_CHEVRON_FORWARD from './assets/ml_chevron_forward.svg';

/** Asset key used by SDK ErrorState/FullPageError - override so Journey/SDK errors show Manulife branding */
const WEB_COMMON_ERROR_STATE_IMAGE = 'WEB_COMMON_ERROR_STATE_IMAGE';

export const getConfig = (
  tokenGetter: () => Promise<string>,
  sponsorId?: string,
): LeagueConfig => ({
  core: {
    clientId: 'manulife-web',
    tenantId: 'manulife',
    auth: {
      getToken: tokenGetter,
    },
    analytics: {
      // These fields are now deprecated, we set out segment key on the server side config (sdk-configs repo)
      // GAPropertyId: import.meta.env.VITE_GA_PROPERTY_ID,
      // segmentKey: import.meta.env.VITE_SEGEMENT_KEY,
      // Manulife is tracking their own events using the following custom methods
      onTrackEvent: (eventName, props) => {
        // eslint-disable-next-line no-console
        console.log('-----calling onTrackEvent', eventName, props);
      },
      onTrackPageViewEvent: (pageName, props) => {
        // eslint-disable-next-line no-console
        console.log('-----calling onTrackPageViewEvent', pageName, props);
      },
    },
    api: {
      wsUrl: import.meta.env.VITE_WS_API_URL as string,
      url: import.meta.env.VITE_API_URL as string,
      legacyRestApi: import.meta.env.VITE_LEGACY_REST_API_URL as string,
    },
    contentUrl: import.meta.env.VITE_CONTENT_URL as string,
    appEnvironment: import.meta.env.VITE_APP_ENV as Environment,
    customMethods: {
      handleLink: async (url) => {
        await window.open(url, '_blank', 'noreferrer');
      },
    },
    ui: {
      theme: manulifeTheme,
      themeOptions: {
        customIcons: {
          tinyHelper: TOOLTIP_ICON,
          interfaceChevronLeft: ML_CHEVRON_BACK,
          interfaceChevronRight: ML_CHEVRON_FORWARD,
          interfaceCTAIndicator:
            'data:image/svg+xml,%3Csvg%20id%3D%22interfaceCTAIndicator%22%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M9.99999%2018C8.41772%2018%206.87103%2017.5308%205.55541%2016.6518C4.23986%2015.7727%203.21447%2014.5233%202.60894%2013.0615C2.00347%2011.5997%201.84503%209.99112%202.15374%208.43927C2.4624%206.88742%203.2243%205.46198%204.34313%204.34314C5.46197%203.22434%206.88744%202.4624%208.43926%202.15372C9.99114%201.84503%2011.5997%202.00348%2013.0615%202.60898C14.5232%203.21448%2015.7727%204.23984%2016.6517%205.55545C17.5308%206.87103%2018%208.41776%2018%2010C17.9977%2012.121%2017.154%2014.1545%2015.6542%2015.6543C14.1545%2017.154%2012.121%2017.9977%209.99999%2018ZM9.35162%206.06983L8.15154%207.3378L10.699%2010.1006L8.10009%2012.7673L9.26782%2014.0698L13.1001%2010.1363L9.35162%206.06983Z%22%20fill%3D%22var(--fill-color%2C%20none)%22%2F%3E%0A%3Cpath%20d%3D%22M10%2019C8.21997%2019%206.47991%2018.4722%204.99987%2017.4832C3.51983%2016.4943%202.36628%2015.0887%201.68509%2013.4442C1.0039%2011.7996%200.82567%209.99002%201.17294%208.24419C1.5202%206.49836%202.37737%204.89472%203.63604%203.63604C4.89471%202.37737%206.49836%201.5202%208.24419%201.17294C9.99002%200.82567%2011.7996%201.0039%2013.4442%201.68509C15.0887%202.36628%2016.4943%203.51983%2017.4832%204.99987C18.4722%206.47991%2019%208.21997%2019%2010C18.9974%2012.3861%2018.0483%2014.6738%2016.361%2016.361C14.6738%2018.0483%2012.3861%2018.9974%2010%2019ZM10%202.67101C8.55046%202.67101%207.13348%203.10084%205.92823%203.90616C4.72298%204.71148%203.7836%205.85612%203.22889%207.19532C2.67418%208.53452%202.52904%2010.0081%202.81183%2011.4298C3.09462%2012.8515%203.79264%2014.1574%204.81762%2015.1824C5.8426%2016.2074%207.1485%2016.9054%208.57018%2017.1882C9.99187%2017.471%2011.4655%2017.3258%2012.8047%2016.7711C14.1439%2016.2164%2015.2885%2015.277%2016.0938%2014.0718C16.8992%2012.8665%2017.329%2011.4495%2017.329%2010C17.3266%208.05696%2016.5537%206.19419%2015.1798%204.82025C13.8058%203.44631%2011.943%202.67339%2010%202.67101Z%22%20fill%3D%22var(--outline-color%2C%20currentColor)%22%2F%3E%0A%3Cpath%20d%3D%22M8.15304%207.3383L9.35308%206.07031L13.1016%2010.1367L9.26931%2014.0703L8.10156%2012.7678L10.7005%2010.1011L8.15304%207.3383Z%22%20fill%3D%22var(--chevron-color%2C%20currentColor)%22%2F%3E%0A%3C%2Fsvg%3E',
        },
      },
    },
    i18n: {
      strings: {
        default: { ...EN_STRING_OVERRIDES },
        fr: { ...FR_STRING_OVERRIDES },
      },
    },
  },
  careDiscovery: {
    masonryAppId: {
      left: 'getCareWeb$root',
    },
    masonryAppVersion: '2.0',
    getCareLandingMasonryQueryParams: {
      ...(sponsorId && { sponsorId }),
    },
  },
  healthJourney: {
    headerConfig: {
      disableBackgroundImage: true,
      alignment: 'start',
      disableUserRewards: true,
    },
    returnToEntrypointAfterExternalLink: true,
  },
  challenges: {
    // Skip the wearable tracking-mode step; enrol directly (manual).
    skipWearableTrackingSelection: true,
    // Show a Close button on the sign-up privacy screen (opens the exit step).
    showSignUpCancelButton: true,
    // Replace "Go to Journey" on the success screen with a Close button.
    showSignUpSuccessCloseButton: true,
    // MANU-5904 — French members resolve to `fr`, so that key is the one that matches.
    privacyPolicyUrl: {
      default:
        'https://www.manulife.ca/privacy-policies/canadian-division-privacy-policy.html',
      fr: 'https://www.manulife.com/ca/fr/a-propos-de-nous/privacy-policy/enonce-de-confidentialite-division-canadienne',
    },
  },
  rewardsEvents: {
    disableAchievementsPageLinks: true,
    alwaysHideSeeAllAchievementsLink: true,
  },
  __internal: {
    shouldApplyCSSReset: false, // Manulife is embedded so we cannot apply a global reset
    shouldApplyLegacyGlobalStyles: false, // Manulife is embedded so we cannot apply a global reset
    shouldIgnoreDOMLangAttributeForLocale: false, // Manulife uses the `lang` attribute to set locale, so we don't want to ignore it
    backButtonComponent: BackButton,
    shouldApplyActivityLayoutFixes: true, // Since Manulife is embedded, we need this so that activities toolbox isn't positioned absolute against nothihng
  },
  assets: {
    overrides: {
      REWARDS_PROGRAM_POINTS: AEROPLAN_ICON,
      [WEB_COMMON_ERROR_STATE_IMAGE]: ERROR_STATE_IMAGE,
      // Challenge sign-up-exit illustration.
      CHALLENGES_MINDFUL_MAN: ALMOST_THERE_ILLUSTRATION,
      // Challenge sign-up success illustration.
      CHALLENGES_HIGH_FIVE: SIGNUP_SUCCESS_ILLUSTRATION,
      // Health activity exit-confirmation modal illustration.
      WAVE_GOODBYE: ALMOST_THERE_ILLUSTRATION,
      // Transparent image removes the success confetti overlay (keeps its box).
      CHALLENGES_CONFETTI: TRANSPARENT_IMAGE,
    },
  },
  rewardsHub: {
    sponsorId,
  },
});

export const initializeNonSDKConfig = () => {};
